import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { formatNumero } from '../lib/utils.ts';
import type { Territorio, EsitoAggiornamentoTerritori } from '@/types/entities/territorio.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'territorio', label: 'Territorio', type: 'text' },
  { key: 'cassaedile', label: 'Cassa edile', type: 'text' },
  { key: 'idTerritorioFenice', label: 'Id territorio Fenice', type: 'text', cssClass: 'text-end' },
  { key: 'idCassaEdileFenice', label: 'Id cassa Fenice', type: 'text', cssClass: 'text-end' },
  { key: 'presenteFenice', label: 'Presente in Fenice', type: 'text' },
  { key: 'aggiornatoAt', label: 'Aggiornato il', type: 'text' },
];

type RigaTerritorio = Territorio & { aggiornatoIl: string };

/** `YYYY-MM-DD HH:MM:SS` in UTC → data e ora italiane nel fuso del browser */
const dataOraLocale = (utc: string): string =>
  new Date(`${utc.replace(' ', 'T')}Z`).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const testoEsito = (e: EsitoAggiornamentoTerritori): string =>
  `${e.totale} coppie, ${e.nuove} nuove, ${e.aggiornate} aggiornate, ${e.nonPiuPresenti} non più presenti in Fenice`;

const testoRicerca = (r: RigaTerritorio): string =>
  `${r.territorio} ${r.cassaedile} ${r.idTerritorioFenice} ${r.idCassaEdileFenice}`.toLowerCase();

/**
 * wc-elenco-territori — Coppie territorio / cassa edile di Fenice in sola lettura (punto 7, pagina /territori).
 *
 * Ricerca rapida nel browser su territorio, cassa e id Fenice; le coppie non più in Fenice
 * hanno la riga evidenziata. "Aggiorna da Fenice" (solo amministratore): esito in toast, poi ricarica.
 */
export class ElencoTerritori extends AppComponent {
  static override componentName = 'wc-elenco-territori';
  protected override get templateId() { return 'elenco-territori'; }

  private dataTable: DataTable | null = null;
  private righe: RigaTerritorio[] = [];
  private filtro = '';

  protected override buildRenderingData(): Record<string, unknown> {
    return { ...super.buildRenderingData(), columnsJson: JSON.stringify(COLUMNS) };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;
    this.listen(this.dataTable, 'dt-filter-change', (e) => {
      this.filtro = (e as CustomEvent<{ filter: string }>).detail.filter.trim().toLowerCase();
      this.mostra();
    });
    this.delegate.on('click', '[data-action="aggiorna-fenice"]', (e) => void this.aggiornaDaFenice(e.target as HTMLElement));
    void this.carica();
  }

  private async carica(): Promise<void> {
    this.dataTable?.setLoading();
    const result = await this.api.territori.lista();
    if (!result.ok) {
      this.dataTable?.setError(result.error);
      return;
    }
    this.righe = result.data.territori.map((t) => ({ ...t, aggiornatoIl: dataOraLocale(t.aggiornatoAt) }));
    this.mostra();
  }

  private mostra(): void {
    const righe = this.filtro ? this.righe.filter((r) => testoRicerca(r).includes(this.filtro)) : this.righe;
    this.dataTable?.setData(righe, { page: 1, totalPages: 1, count: righe.length, pageSize: righe.length });
    const nonPresenti = this.righe.filter((r) => !r.presenteFenice).length;
    const conteggio = this.$('[data-conteggio-territori]');
    if (conteggio) {
      conteggio.textContent = `${formatNumero(this.righe.length)} coppie` +
        (nonPresenti ? `, ${formatNumero(nonPresenti)} non più in Fenice` : '');
    }
  }

  private async aggiornaDaFenice(btn: HTMLElement): Promise<void> {
    const loading = Weext.LoadingButton.getInstance(btn);
    loading?.start();
    const result = await this.api.territori.aggiorna();
    loading?.stop();
    if (!result.ok) {
      await Weext.alert(result.error, { title: 'Aggiornamento non riuscito' });
      return;
    }
    Weext.toast(testoEsito(result.data), { type: 'success' });
    await this.carica();
  }
}
